import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ProjectsService } from 'src/app/services/projects.service';
import Swal from 'sweetalert2';

const pdfjsLib: any = require('pdfjs-dist/build/pdf.js');

@Component({
  selector: 'app-shared-arts-diagram-upload',
  templateUrl: './shared-arts-diagram-upload.component.html',
  styleUrls: ['./shared-arts-diagram-upload.component.scss']
})
export class SharedArtsDiagramUploadComponent implements OnInit {
  private readonly toleranciaProporcionImagen = 0.05;
  form!: FormGroup;
  cargando = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private projectService: ProjectsService,
    private dialogRef: MatDialogRef<SharedArtsDiagramUploadComponent>
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      blob: [null, Validators.required]
    });
  }

  onValueChange(archivos: File[]): void {
    const seleccion = archivos || [];
    if (!seleccion.length) {
      this.form.controls.blob.setValue(null, { emitEvent: false });
      return;
    }

    const archivosPermitidos = seleccion.filter(archivo => this.esArchivoPermitido(archivo));

    if (archivosPermitidos.length !== seleccion.length) {
      Swal.fire({
        icon: 'warning',
        title: 'Archivo no válido',
        text: 'Solo se permiten archivos JPG, JPEG, PNG, WEBP o un PDF.'
      });
      this.form.controls.blob.setValue(null, { emitEvent: false });
      return;
    }

    if (!this.esSeleccionValida(archivosPermitidos)) {
      Swal.fire({
        icon: 'warning',
        title: 'Combinación no válida',
        text: 'Puedes subir un PDF o una o varias imágenes, pero no combinar PDF con imágenes ni subir varios PDF.'
      });
      this.form.controls.blob.setValue(null, { emitEvent: false });
      return;
    }

    this.form.controls.blob.setValue(archivosPermitidos.length ? archivosPermitidos : null, { emitEvent: false });
  }

  async subirArchivo(): Promise<void> {
    const archivos: File[] = this.form.value.blob || [];
    if (!archivos.length || !this.esSeleccionValida(archivos)) {
      Swal.fire({
        icon: 'warning',
        title: 'Archivo requerido',
        text: 'Selecciona un PDF o una o varias imágenes antes de continuar.'
      });
      return;
    }

    this.cargando = true;

    const configuracionAnterior = this.data.archivo_vigente?.selected_config;
    let conservarConfiguracion = false;

    if (configuracionAnterior) {
      const tipoAnterior = this.data.archivo_vigente?.tipo;
      const tipoNuevo = this.tipoSeleccion(archivos);
      let motivoReinicio = '';

      if (tipoAnterior !== tipoNuevo) {
        motivoReinicio = 'El nuevo archivo es de un tipo diferente al arte vigente.';
      } else if (tipoNuevo === 'pdf') {
        try {
          const paginasAnteriores = this.obtenerPaginasConfiguracion(configuracionAnterior);
          const paginasNuevas = await this.obtenerNumeroPaginasPdf(archivos[0]);
          if (!paginasAnteriores || paginasAnteriores !== paginasNuevas) {
            motivoReinicio = paginasAnteriores
              ? `El PDF vigente tiene ${paginasAnteriores} página(s) y el nuevo tiene ${paginasNuevas}.`
              : 'No fue posible comprobar la cantidad de páginas guardada en la configuración anterior.';
          } else {
            conservarConfiguracion = true;
          }
        } catch (error) {
          console.error('No fue posible validar el PDF seleccionado.', error);
          Swal.fire({
            icon: 'error',
            title: 'PDF no válido',
            text: 'No fue posible leer el PDF seleccionado. Verifica que no esté dañado o protegido.'
          });
          this.cargando = false;
          return;
        }
      } else if (tipoNuevo === 'imagenes') {
        const paginasAnteriores = this.obtenerPaginasConfiguracion(configuracionAnterior);
        if (paginasAnteriores === archivos.length) {
          conservarConfiguracion = true;
        } else {
          motivoReinicio = `El arte vigente tiene ${paginasAnteriores || 0} imagen(es) y el nuevo tiene ${archivos.length}.`;
        }
      } else {
        try {
          const dimensionesAnteriores = this.obtenerDimensionesConfiguracion(configuracionAnterior);
          const dimensionesNuevas = await this.obtenerDimensionesImagen(archivos[0]);
          if (!dimensionesAnteriores) {
            motivoReinicio = 'No fue posible comprobar las dimensiones guardadas en la configuración anterior.';
          } else if (!this.sonProporcionesCompatibles(dimensionesAnteriores, dimensionesNuevas)) {
            motivoReinicio = 'La nueva imagen tiene dimensiones diferentes a las del arte vigente.';
          } else {
            conservarConfiguracion = true;
          }
        } catch (error) {
          console.error('No fue posible validar la imagen seleccionada.', error);
          Swal.fire({
            icon: 'error',
            title: 'Imagen no válida',
            text: 'No fue posible leer las dimensiones de la imagen seleccionada. Verifica que el archivo no esté dañado.'
          });
          this.cargando = false;
          return;
        }
      }

      if (motivoReinicio) {
        const confirmacion = await Swal.fire({
          icon: 'warning',
          title: 'Las anotaciones no se conservarán',
          html: `${motivoReinicio}<br><br>Será necesario volver a realizar las anotaciones. ¿Deseas continuar?`,
          showCancelButton: true,
          confirmButtonText: 'Continuar y reiniciar',
          cancelButtonText: 'Cancelar'
        });
        if (!confirmacion.isConfirmed) {
          this.cargando = false;
          return;
        }
      }
    }

    const form = new FormData();
    if (archivos.length === 1) {
      form.append('blob', archivos[0], archivos[0].name);
    } else {
      archivos.forEach((archivo, indice) => form.append(`blob${indice}`, archivo, archivo.name));
    }
    form.append('folder', String(this.data.folder || ''));
    form.append('id_proyecto_campana', String(this.data.id_proyecto_campana));
    form.append('id_medio', String(this.data.id_medio));
    form.append('id_pop', String(this.data.id_pop));
    form.append('id_fase', String(this.data.id_fase));

    if (conservarConfiguracion) {
      form.append(
        'selected_config',
        typeof configuracionAnterior === 'string'
          ? configuracionAnterior
          : JSON.stringify(configuracionAnterior)
      );
    }

    Swal.fire({
      icon: 'info',
      title: 'Subiendo archivo...',
      text: 'Espera mientras termina la carga.',
      allowEscapeKey: false,
      allowOutsideClick: false,
      showConfirmButton: false,
      didOpen: () => Swal.showLoading()
    });

    this.projectService.uploadSharedDiagramFile(form).subscribe({
      next: (resultado: any) => {
        this.cargando = false;
        if (resultado?.message === 'Success') {
          Swal.fire({
            icon: 'success',
            title: 'Archivo subido',
            text: 'El archivo se guardó correctamente.'
          }).then(() => this.dialogRef.close({ reload: true }));
          return;
        }

        Swal.fire({
          icon: 'error',
          title: 'Error al guardar',
          text: resultado?.data || 'No fue posible guardar la imagen.'
        });
      },
      error: (error: any) => {
        this.cargando = false;
        Swal.fire({
          icon: 'error',
          title: 'Error al guardar',
          text: error?.error?.data || 'No fue posible guardar la imagen. Inténtalo nuevamente.'
        });
      }
    });
  }

  private esArchivoPermitido(archivo: File): boolean {
    const extensiones = ['.jpg', '.jpeg', '.png', '.webp'];
    const nombre = archivo.name.toLowerCase();
    const tiposImagen = ['image/jpeg', 'image/png', 'image/webp'];
    const esImagen = tiposImagen.includes(String(archivo.type || '').toLowerCase())
      || extensiones.some(extension => nombre.endsWith(extension));
    const esPdf = archivo.type === 'application/pdf' || nombre.endsWith('.pdf');
    return esImagen || esPdf;
  }

  private esSeleccionValida(archivos: File[]): boolean {
    if (!archivos.length || archivos.some(archivo => !this.esArchivoPermitido(archivo))) return false;
    const cantidadPdf = archivos.filter(archivo => this.esPdf(archivo)).length;
    return cantidadPdf === 0 || (cantidadPdf === 1 && archivos.length === 1);
  }

  private esPdf(archivo: File): boolean {
    const nombre = String(archivo?.name || '').toLowerCase();
    return archivo?.type === 'application/pdf' || nombre.endsWith('.pdf');
  }

  private tipoSeleccion(archivos: File[]): string {
    if (this.esPdf(archivos[0])) return 'pdf';
    return archivos.length > 1 ? 'imagenes' : 'imagen';
  }

  private obtenerPaginasConfiguracion(configuracion: any): number {
    let valor = configuracion;
    if (typeof valor === 'string') {
      try {
        valor = JSON.parse(valor);
      } catch {
        return 0;
      }
    }
    return Number(valor?.archivo?.paginas || 0);
  }

  private obtenerDimensionesConfiguracion(configuracion: any): { ancho: number; alto: number } | null {
    let valor = configuracion;
    if (typeof valor === 'string') {
      try {
        valor = JSON.parse(valor);
      } catch {
        return null;
      }
    }

    const ancho = Number(valor?.imagen?.ancho || 0);
    const alto = Number(valor?.imagen?.alto || 0);
    return ancho > 0 && alto > 0 ? { ancho, alto } : null;
  }

  private obtenerDimensionesImagen(archivo: File): Promise<{ ancho: number; alto: number }> {
    const url = URL.createObjectURL(archivo);
    return new Promise((resolve, reject) => {
      const imagen = new Image();
      imagen.onload = () => {
        const dimensiones = { ancho: imagen.naturalWidth, alto: imagen.naturalHeight };
        URL.revokeObjectURL(url);
        if (dimensiones.ancho > 0 && dimensiones.alto > 0) resolve(dimensiones);
        else reject(new Error('La imagen no contiene dimensiones válidas.'));
      };
      imagen.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('No fue posible cargar la imagen.'));
      };
      imagen.src = url;
    });
  }

  private sonProporcionesCompatibles(
    anterior: { ancho: number; alto: number },
    nueva: { ancho: number; alto: number }
  ): boolean {
    const proporcionAnterior = anterior.ancho / anterior.alto;
    const proporcionNueva = nueva.ancho / nueva.alto;
    const diferenciaRelativa = Math.abs(proporcionNueva - proporcionAnterior) / proporcionAnterior;
    return diferenciaRelativa <= this.toleranciaProporcionImagen;
  }

  private async obtenerNumeroPaginasPdf(archivo: File): Promise<number> {
    if (!(globalThis as any).pdfjsWorker?.WorkerMessageHandler) {
      require('pdfjs-dist/build/pdf.worker.js');
    }

    const carga = pdfjsLib.getDocument({
      data: new Uint8Array(await archivo.arrayBuffer()),
      isEvalSupported: false
    });
    const documento = await carga.promise;
    try {
      return Number(documento.numPages || 0);
    } finally {
      if (typeof documento?.destroy === 'function') await documento.destroy();
    }
  }
}
